/**
 * Panel Resize — makes all panel dividers draggable
 * Horizontal divider (.ws-divider) resizes top/bottom rows
 * Vertical dividers (.panel-divider-v) resize adjacent panels
 */

export function initPanelResize(): void {
  // Horizontal divider between top and bottom rows
  const wsDivider = document.querySelector('.ws-divider') as HTMLElement;
  if (wsDivider) {
    initHorizontalResize(wsDivider);
  }

  // Vertical dividers in top row
  const topRow = document.querySelector('.ws-row.top') as HTMLElement;
  if (topRow) {
    topRow.querySelectorAll<HTMLElement>('.panel-divider-v').forEach((div) => {
      initVerticalResize(div);
    });
  }

  // Vertical dividers in bottom row
  const bottomRow = document.querySelector('.ws-row.bottom') as HTMLElement;
  if (bottomRow) {
    bottomRow.querySelectorAll<HTMLElement>('.panel-divider-v').forEach((div) => {
      initVerticalResize(div);
    });
  }
}

export function initHorizontalResize(divider: HTMLElement): void {
  let startY = 0;
  let topStartH = 0;
  let bottomStartH = 0;
  let topRow: HTMLElement;
  let bottomRow: HTMLElement;

  divider.addEventListener('mousedown', (e) => {
    e.preventDefault();
    topRow = divider.previousElementSibling as HTMLElement;
    bottomRow = divider.nextElementSibling as HTMLElement;
    if (!topRow || !bottomRow) return;

    startY = e.clientY;
    topStartH = topRow.getBoundingClientRect().height;
    bottomStartH = bottomRow.getBoundingClientRect().height;

    // Remove flex ratios, switch to explicit heights
    topRow.style.flex = 'none';
    bottomRow.style.flex = 'none';
    topRow.style.height = `${topStartH}px`;
    bottomRow.style.height = `${bottomStartH}px`;

    document.body.classList.add('resizing-v');
    document.addEventListener('mousemove', onHMove);
    document.addEventListener('mouseup', onHUp);
  });

  function onHMove(e: MouseEvent): void {
    const delta = e.clientY - startY;
    const newTop = Math.max(100, topStartH + delta);
    const newBottom = Math.max(80, bottomStartH - delta);

    // Only apply if both fit
    if (newTop >= 100 && newBottom >= 80) {
      topRow.style.height = `${newTop}px`;
      bottomRow.style.height = `${newBottom}px`;
    }
  }

  function onHUp(): void {
    document.body.classList.remove('resizing-v');
    document.removeEventListener('mousemove', onHMove);
    document.removeEventListener('mouseup', onHUp);
    // Trigger resize so canvases re-scale
    window.dispatchEvent(new Event('resize'));
  }
}

export function initVerticalResize(divider: HTMLElement): void {
  let startX = 0;
  let leftStartW = 0;
  let rightStartW = 0;
  let leftPanel: HTMLElement;
  let rightPanel: HTMLElement;

  divider.addEventListener('mousedown', (e) => {
    e.preventDefault();

    // Find visible adjacent panels (skip hidden ones)
    leftPanel = findVisibleSibling(divider, 'previous') as HTMLElement;
    rightPanel = findVisibleSibling(divider, 'next') as HTMLElement;
    if (!leftPanel || !rightPanel) return;

    startX = e.clientX;
    leftStartW = leftPanel.getBoundingClientRect().width;
    rightStartW = rightPanel.getBoundingClientRect().width;

    // Remove flex ratios, switch to explicit widths
    leftPanel.style.flex = 'none';
    rightPanel.style.flex = 'none';
    leftPanel.style.width = `${leftStartW}px`;
    rightPanel.style.width = `${rightStartW}px`;

    document.body.classList.add('resizing-h');
    document.addEventListener('mousemove', onVMove);
    document.addEventListener('mouseup', onVUp);
  });

  function onVMove(e: MouseEvent): void {
    const delta = e.clientX - startX;
    const minW = 80;
    const newLeft = Math.max(minW, leftStartW + delta);
    const newRight = Math.max(minW, rightStartW - delta);

    if (newLeft >= minW && newRight >= minW) {
      leftPanel.style.width = `${newLeft}px`;
      rightPanel.style.width = `${newRight}px`;
    }
  }

  function onVUp(): void {
    document.body.classList.remove('resizing-h');
    document.removeEventListener('mousemove', onVMove);
    document.removeEventListener('mouseup', onVUp);
    window.dispatchEvent(new Event('resize'));
  }
}

/** Walk siblings to find the nearest visible panel or ws-main (not divider, not hidden) */
function findVisibleSibling(el: HTMLElement, direction: 'previous' | 'next'): HTMLElement | null {
  let sibling = direction === 'previous' ? el.previousElementSibling : el.nextElementSibling;
  while (sibling) {
    const s = sibling as HTMLElement;
    if (
      (s.classList.contains('panel') || s.classList.contains('ws-main')) &&
      s.style.display !== 'none'
    ) {
      return s;
    }
    sibling = direction === 'previous' ? sibling.previousElementSibling : sibling.nextElementSibling;
  }
  return null;
}
